import {inject, Injectable, signal} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {SelectData} from "../../../../core/types/records-table.types";
import {TelegramSelectDataService} from "./telegram-select-data.service";
import {TelegramChatsService} from "./telegram-chats.service";
import {TelegramAccountsService} from "./telegram-accounts.service";
import {SaveFileParams} from "../types/telegram-save-file-modal.types";

@Injectable({
  providedIn: 'root'
})
export class TelegramSaveFileModalService {
  private telegramSelectDataService: TelegramSelectDataService = inject(TelegramSelectDataService);
  private telegramChatsService: TelegramChatsService = inject(TelegramChatsService);
  private telegramAccountsService: TelegramAccountsService = inject(TelegramAccountsService);
  private _recordCalenderIdSelectData = signal<Array<SelectData>>([]);
  private _fileDestinationsSelectData = signal<Array<SelectData>>([]);
  public recordCalenderIdSelectData = this._recordCalenderIdSelectData.asReadonly();
  public fileDestinationsSelectData = this._fileDestinationsSelectData.asReadonly();

  public initForm(): FormGroup {
    return new FormGroup({
      recordCalendarId: new FormControl('', [
        Validators.required
      ]),
      destinationType: new FormControl('', [
        Validators.required
      ])
    });
  }

  public scrollSelectedOptionIntoView(index: number): void {
    const optionElements = document.querySelectorAll('mat-option');

    if (!optionElements[index]) { return; }

    optionElements[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  public async initialize(chatId: number): Promise<void> {
    await Promise.all([
      this.setSelectRecordCalendarIdData(chatId),
      this.setSelectFileDestinationData()
    ]);
  }

  public getSelectData(key: string, searchValue: string): Array<SelectData> {
    if (searchValue) {
      return key === 'recordCalendarId' ?
        this.recordCalenderIdSelectData().filter(i => i.value.includes(searchValue) || i.id.toString().includes(searchValue)) :
        this.fileDestinationsSelectData();
    }

    return key === 'recordCalendarId' ?
      this.recordCalenderIdSelectData() :
      this.fileDestinationsSelectData();
  }

  public setSaveFileParams(formGroup: FormGroup, messageId: number): SaveFileParams {
    return {
      recordCalendarId: formGroup.get('recordCalendarId' as string)?.value,
      accountId: this.telegramAccountsService.selectedAccount().accountId,
      messageId: messageId,
      chatId: this.telegramChatsService.chat()?.id || 0,
      destinationType: formGroup.get('destinationType' as string)?.value
    }
  }

  private async setSelectRecordCalendarIdData(chatId: number): Promise<void> {
    this._recordCalenderIdSelectData.set([]);
    const data = await this.telegramSelectDataService.getSelectRecordCalendarIdData(chatId, this.telegramAccountsService.selectedAccount().accountId).catch(() => []);
    this._recordCalenderIdSelectData.set(data);
  }

  private async setSelectFileDestinationData(): Promise<void> {
    this._fileDestinationsSelectData.set([]);
    await this.telegramSelectDataService.getSelectFileDestinationData()
      .then(selectData => selectData.length && this.processSelectData(selectData))
      .catch(() => this._fileDestinationsSelectData.set([]));
  }

  private processSelectData(selectData: Array<SelectData>): void {
    const data = Object.values(selectData);
    const initialStatsIndex = data.findIndex(item => item.id.toString() === "INITIAL_STATISTICS");

    if (initialStatsIndex) {
      const [initialStats] = data.splice(initialStatsIndex, 1);
      data.unshift(initialStats);
    }

    this._fileDestinationsSelectData.set(data);
  }
}
